/**
 * GENERATED - DO NOT EDIT.
 * Queryable form of the compliance framework crosswalk: framework editions, the per-framework
 * reference catalogs, the signal index the read-time join keys off, and the no-current-mapping
 * declarations, plus the crosswalk version and content hash.
 * Source: packages/contract/crosswalk/*.yaml. Regenerate with `npm run crosswalk:generate`.
 * Crosswalk version: 1.0.0
 * Crosswalk hash: sha256-LaroDeljXCFzsNMbHPyu16nCDUk_8rwC-qNovww5ASA
 */

import type { Confidence, FrameworkId, SignalKind } from "../src/crosswalk/ids";

export const CROSSWALK_VERSION = "1.0.0" as const;
export const CROSSWALK_HASH = "sha256-LaroDeljXCFzsNMbHPyu16nCDUk_8rwC-qNovww5ASA" as const;

/**
 * The published edition the crosswalk records for each framework (Requirement 1.6). Carried
 * onto every reference below so two editions can coexist during a transition (Requirement 6.2).
 */
export const FRAMEWORK_EDITIONS: Readonly<Record<FrameworkId, { readonly edition: string }>> =
  Object.freeze({
    mitre_atlas: Object.freeze({ edition: "4.5.0" }),
    owasp_llm_top10: Object.freeze({ edition: "2025" }),
    nist_ai_rmf: Object.freeze({ edition: "1.0" }),
    nist_800_53: Object.freeze({ edition: "Rev. 5" }),
  });

/** One framework reference: its id, human-readable title, and the edition it belongs to. */
export interface FrameworkReference {
  readonly id: string;
  readonly title: string;
  readonly edition: string;
}

/**
 * Each framework's reference catalog, id → title + edition. A reference id may appear more than
 * once when the same id recurs under a different edition during a transition (Requirement 6.2),
 * so both editions survive — the list is keyed by (id, edition), not id alone.
 */
export const FRAMEWORK_REFERENCES: Readonly<
  Record<FrameworkId, readonly FrameworkReference[]>
> = Object.freeze({
  mitre_atlas: Object.freeze([
    { id: "AML.TA0000", title: "ML Model Access", edition: "4.5.0" },
    { id: "AML.TA0002", title: "Reconnaissance", edition: "4.5.0" },
    { id: "AML.TA0004", title: "Initial Access", edition: "4.5.0" },
    { id: "AML.T0010", title: "ML Supply Chain Compromise", edition: "4.5.0" },
    { id: "AML.T0024", title: "Exfiltration via ML Inference API", edition: "4.5.0" },
    { id: "AML.T0043", title: "Craft Adversarial Data", edition: "4.5.0" },
    { id: "AML.T0051", title: "LLM Prompt Injection", edition: "4.5.0" },
    { id: "AML.T0054", title: "LLM Jailbreak", edition: "4.5.0" },
    { id: "AML.T0057", title: "LLM Data Leakage", edition: "4.5.0" },
    { id: "AML.T0010.000", title: "ML Supply Chain Compromise: Hardware", edition: "4.5.0" },
    { id: "AML.T0051.000", title: "LLM Prompt Injection: Direct", edition: "4.5.0" },
    { id: "AML.T0051.001", title: "LLM Prompt Injection: Indirect", edition: "4.5.0" },
  ]),
  owasp_llm_top10: Object.freeze([
    { id: "LLM01", title: "Prompt Injection", edition: "2025" },
    { id: "LLM02", title: "Sensitive Information Disclosure", edition: "2025" },
    { id: "LLM03", title: "Supply Chain", edition: "2025" },
    { id: "LLM04", title: "Data and Model Poisoning", edition: "2025" },
    { id: "LLM05", title: "Improper Output Handling", edition: "2025" },
    { id: "LLM06", title: "Excessive Agency", edition: "2025" },
    { id: "LLM07", title: "System Prompt Leakage", edition: "2025" },
    { id: "LLM08", title: "Vector and Embedding Weaknesses", edition: "2025" },
    { id: "LLM09", title: "Misinformation", edition: "2025" },
    { id: "LLM10", title: "Unbounded Consumption", edition: "2025" },
  ]),
  nist_ai_rmf: Object.freeze([
    { id: "GOVERN.1.1", title: "Legal and regulatory requirements involving AI are understood, managed, and documented", edition: "1.0" },
    { id: "GOVERN.1.2", title: "Trustworthy AI characteristics are integrated into organizational policies and practices", edition: "1.0" },
    { id: "GOVERN.4.1", title: "Policies foster a critical thinking and safety-first mindset in AI design and deployment", edition: "1.0" },
    { id: "GOVERN.6.1", title: "Policies address AI risks arising from third-party software, data, and supply chain", edition: "1.0" },
    { id: "MAP.1.1", title: "Intended purpose, context of use, and expectations of the AI system are understood", edition: "1.0" },
    { id: "MAP.4.1", title: "Approaches for mapping AI technology and legal risks of its components are in place", edition: "1.0" },
    { id: "MAP.5.1", title: "Likelihood and magnitude of each identified impact are determined", edition: "1.0" },
    { id: "MEASURE.2.6", title: "AI system is evaluated for safety risks and residual negative risk", edition: "1.0" },
    { id: "MEASURE.2.7", title: "AI system security and resilience are evaluated and documented", edition: "1.0" },
    { id: "MEASURE.2.9", title: "AI model is explained, validated, and documented; AI output is interpreted", edition: "1.0" },
    { id: "MANAGE.1.3", title: "Responses to high-priority AI risks are developed, planned, and documented", edition: "1.0" },
    { id: "MANAGE.2.1", title: "Resources are allocated to mapped and measured AI risks regularly", edition: "1.0" },
    { id: "MANAGE.4.1", title: "Post-deployment monitoring plans are implemented, including incident response", edition: "1.0" },
  ]),
  nist_800_53: Object.freeze([
    { id: "AC-2", title: "Account Management", edition: "Rev. 5" },
    { id: "AC-2 (1)", title: "Account Management | Automated System Account Management", edition: "Rev. 5" },
    { id: "AC-3", title: "Access Enforcement", edition: "Rev. 5" },
    { id: "AC-6", title: "Least Privilege", edition: "Rev. 5" },
    { id: "AU-2", title: "Event Logging", edition: "Rev. 5" },
    { id: "AU-6", title: "Audit Record Review, Analysis, and Reporting", edition: "Rev. 5" },
    { id: "CM-6", title: "Configuration Settings", edition: "Rev. 5" },
    { id: "IA-2", title: "Identification and Authentication (Organizational Users)", edition: "Rev. 5" },
    { id: "IA-5", title: "Authenticator Management", edition: "Rev. 5" },
    { id: "RA-5", title: "Vulnerability Monitoring and Scanning", edition: "Rev. 5" },
    { id: "SA-11", title: "Developer Testing and Evaluation", edition: "Rev. 5" },
    { id: "SC-28", title: "Protection of Information at Rest", edition: "Rev. 5" },
    { id: "SI-2", title: "Flaw Remediation", edition: "Rev. 5" },
    { id: "SI-4", title: "System Monitoring", edition: "Rev. 5" },
    { id: "SI-10", title: "Information Input Validation", edition: "Rev. 5" },
    { id: "SR-3", title: "Supply Chain Controls and Processes", edition: "Rev. 5" },
    { id: "SR-4", title: "Provenance", edition: "Rev. 5" },
  ]),
});

/** One authored crosswalk entry relating a single Oathera signal to a single reference. */
export interface CrosswalkEntry {
  readonly signal: { readonly kind: SignalKind; readonly value: string };
  readonly framework: FrameworkId;
  readonly reference: string;
  readonly confidence: Confidence;
  readonly rationale: string;
}

/**
 * Signal index for the read-time join: `${kind}\u0000${value}` → the entries that key off that
 * signal, in authored order. A finding's signal is looked up here to find every framework
 * reference it crosswalks to; a signal absent from the map is an unmapped finding.
 */
export const CROSSWALK_INDEX: ReadonlyMap<string, readonly CrosswalkEntry[]> = new Map<
  string,
  readonly CrosswalkEntry[]
>([
  ["finding_code\u0000long_lived_credentials", Object.freeze([
    { signal: { kind: "finding_code", value: "long_lived_credentials" }, framework: "nist_800_53", reference: "IA-5", confidence: "direct", rationale: "Long-lived credentials map to Authenticator Management lifecycle controls." },
  ])],
  ["finding_code\u0000overprivileged", Object.freeze([
    { signal: { kind: "finding_code", value: "overprivileged" }, framework: "nist_800_53", reference: "AC-6", confidence: "direct", rationale: "Overprivileged identities map directly to the Least Privilege control." },
    { signal: { kind: "finding_code", value: "overprivileged" }, framework: "nist_ai_rmf", reference: "MEASURE.2.7", confidence: "related", rationale: "Overprivileged identities relate to measuring system security and resilience." },
  ])],
  ["finding_code\u0000improper_offboarding", Object.freeze([
    { signal: { kind: "finding_code", value: "improper_offboarding" }, framework: "nist_800_53", reference: "AC-2", confidence: "direct", rationale: "Residual access after offboarding maps to Account Management deprovisioning." },
  ])],
  ["finding_code\u0000stale_identity", Object.freeze([
    { signal: { kind: "finding_code", value: "stale_identity" }, framework: "nist_800_53", reference: "AC-2", confidence: "related", rationale: "Stale, inactive identities relate to Account Management review and disabling." },
  ])],
  ["finding_code\u0000unowned_identity", Object.freeze([
    { signal: { kind: "finding_code", value: "unowned_identity" }, framework: "nist_800_53", reference: "AC-2", confidence: "related", rationale: "Unowned identities relate to Account Management ownership and accountability." },
  ])],
  ["assurance_engine\u0000sca", Object.freeze([
    { signal: { kind: "assurance_engine", value: "sca" }, framework: "nist_800_53", reference: "SR-3", confidence: "related", rationale: "Software-composition findings relate to Supply Chain Risk Management controls." },
    { signal: { kind: "assurance_engine", value: "sca" }, framework: "owasp_llm_top10", reference: "LLM03", confidence: "related", rationale: "Software-composition findings relate to LLM supply-chain weaknesses." },
    { signal: { kind: "assurance_engine", value: "sca" }, framework: "nist_ai_rmf", reference: "GOVERN.6.1", confidence: "related", rationale: "Software-composition findings relate to third-party and supply-chain policy." },
  ])],
  ["assurance_engine\u0000sbom", Object.freeze([
    { signal: { kind: "assurance_engine", value: "sbom" }, framework: "nist_800_53", reference: "SR-4", confidence: "related", rationale: "SBOM provenance findings relate to Supply Chain provenance controls." },
    { signal: { kind: "assurance_engine", value: "sbom" }, framework: "owasp_llm_top10", reference: "LLM03", confidence: "related", rationale: "SBOM/provenance findings relate to LLM supply-chain weaknesses." },
  ])],
  ["assurance_engine\u0000iac", Object.freeze([
    { signal: { kind: "assurance_engine", value: "iac" }, framework: "nist_800_53", reference: "CM-6", confidence: "related", rationale: "Infrastructure-as-code findings relate to secure Configuration Settings." },
  ])],
  ["assurance_engine\u0000sast", Object.freeze([
    { signal: { kind: "assurance_engine", value: "sast" }, framework: "nist_800_53", reference: "SA-11", confidence: "related", rationale: "Static analysis findings relate to Developer Testing and Evaluation controls." },
    { signal: { kind: "assurance_engine", value: "sast" }, framework: "owasp_llm_top10", reference: "LLM05", confidence: "informational", rationale: "Static-analysis findings may relate to improper output handling." },
  ])],
  ["cwe\u0000CWE-20", Object.freeze([
    { signal: { kind: "cwe", value: "CWE-20" }, framework: "nist_800_53", reference: "SI-10", confidence: "direct", rationale: "Improper input validation maps to Information Input Validation." },
  ])],
  ["assurance_engine\u0000secrets", Object.freeze([
    { signal: { kind: "assurance_engine", value: "secrets" }, framework: "owasp_llm_top10", reference: "LLM02", confidence: "related", rationale: "Secret-scanning findings relate to sensitive information disclosure." },
    { signal: { kind: "assurance_engine", value: "secrets" }, framework: "mitre_atlas", reference: "AML.T0057", confidence: "informational", rationale: "Secret exposure is informationally related to LLM data leakage techniques." },
  ])],
  ["cwe\u0000CWE-798", Object.freeze([
    { signal: { kind: "cwe", value: "CWE-798" }, framework: "owasp_llm_top10", reference: "LLM02", confidence: "direct", rationale: "Hard-coded credentials map to sensitive information disclosure." },
  ])],
  ["cwe\u0000CWE-77", Object.freeze([
    { signal: { kind: "cwe", value: "CWE-77" }, framework: "owasp_llm_top10", reference: "LLM01", confidence: "informational", rationale: "Command-injection weaknesses are informationally related to prompt injection." },
    { signal: { kind: "cwe", value: "CWE-77" }, framework: "mitre_atlas", reference: "AML.T0051", confidence: "informational", rationale: "Command-injection weaknesses are informationally related to LLM prompt injection." },
  ])],
  ["posture_kind\u0000nhi", Object.freeze([
    { signal: { kind: "posture_kind", value: "nhi" }, framework: "nist_ai_rmf", reference: "MAP.1.1", confidence: "informational", rationale: "Non-human-identity posture informs understanding of the system's context of use." },
  ])],
  ["posture_kind\u0000host", Object.freeze([
    { signal: { kind: "posture_kind", value: "host" }, framework: "nist_ai_rmf", reference: "MEASURE.2.7", confidence: "informational", rationale: "Host posture findings inform evaluation of system security and resilience." },
  ])],
  ["finding_code\u0000host_changed", Object.freeze([
    { signal: { kind: "finding_code", value: "host_changed" }, framework: "nist_ai_rmf", reference: "MANAGE.4.1", confidence: "informational", rationale: "Host-change findings inform post-deployment monitoring and incident response." },
  ])],
  ["cwe\u0000CWE-200", Object.freeze([
    { signal: { kind: "cwe", value: "CWE-200" }, framework: "mitre_atlas", reference: "AML.T0057", confidence: "informational", rationale: "Information-exposure weaknesses are informationally related to LLM data leakage." },
  ])],
]);

/**
 * Each framework's explicit no-current-mapping reason, or `null` when the framework is mapped
 * by at least one entry. Every framework is present — coverage is complete and explicit
 * (Requirement 1.4).
 */
export const NO_CURRENT_MAPPING: Readonly<Record<FrameworkId, string | null>> = Object.freeze({
  mitre_atlas: null,
  owasp_llm_top10: null,
  nist_ai_rmf: null,
  nist_800_53: null,
});
